import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  InputAdornment
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { CloudUpload as UploadIcon } from '@mui/icons-material';
import { Company, InvoiceCreate } from '../../types/models';
import apiService from '../../services/api';

const InvoiceUploadForm: React.FC = () => {
  const navigate = useNavigate();
  // const [companies, setCompanies] = useState<Company[]>([]); // No longer needed - admin assigns company
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [frontImagePreview, setFrontImagePreview] = useState<string | null>(null);
  const [backImagePreview, setBackImagePreview] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<{
    // company_id: number | null; // Removed - admin assigns during verification
    invoice_number: string;
    issue_date: Date | null;
    due_date: Date | null;
    amount: string;
    tax_retention: number;
    tax_amount: number;
    currency: string;
    description: string;
    front_image: File | null;
    back_image: File | null;
  }>({
    // company_id: null, // Removed
    invoice_number: '',
    issue_date: null,
    due_date: null,
    amount: '',
    tax_retention: 0,
    tax_amount: 0,
    currency: 'PYG',
    description: '',
    front_image: null,
    back_image: null,
  });

  // Form validation
  const [formErrors, setFormErrors] = useState<{
    // company_id?: string; // Removed - no longer validated
    invoice_number?: string;
    issue_date?: string;
    due_date?: string;
    amount?: string;
    tax_retention?: string;
    tax_amount?: string;
    front_image?: string;
  }>({});

  // Fetch companies for dropdown - No longer needed
  // useEffect(() => {
  //   const fetchCompanies = async () => {
  //     try {
  //       const data = await apiService.get<Company[]>('/companies');
  //       setCompanies(data);
  //     } catch (err) {
  //       console.error('Error fetching companies:', err);
  //       setError('Failed to load companies. Please try again later.');
  //     }
  //   };
  //
  //   fetchCompanies();
  // }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: name === 'tax_amount' ? (value === '' ? 0 : Number(value)) : value,
    });
  };

  const handleSelectChange = (e: React.ChangeEvent<{ name?: string; value: unknown }> | any) => {
    const name = e.target.name as string;
    const value = e.target.value;
    setFormData({
      ...formData,
      [name]: name === 'tax_retention' ? Number(value) : value,
    });
  };

  const handleDateChange = (date: Date | null, fieldName: string) => {
    setFormData({
      ...formData,
      [fieldName]: date,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, side: 'front' | 'back') => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      
      // Create a preview URL
      const previewUrl = URL.createObjectURL(file);
      
      if (side === 'front') {
        setFormData({
          ...formData,
          front_image: file,
        });
        setFrontImagePreview(previewUrl);
      } else {
        setFormData({
          ...formData,
          back_image: file,
        });
        setBackImagePreview(previewUrl);
      }
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    // Company is now optional - admin will assign during verification
    // if (!formData.company_id) {
    //   errors.company_id = 'Please select a company';
    // }

    if (!formData.invoice_number) {
      errors.invoice_number = 'El número de factura es requerido';
    }

    if (!formData.issue_date) {
      errors.issue_date = 'La fecha de emisión es requerida';
    }

    if (!formData.due_date) {
      errors.due_date = 'La fecha de vencimiento es requerida';
    } else if (formData.issue_date && formData.due_date < formData.issue_date) {
      errors.due_date = 'La fecha de vencimiento debe ser posterior a la fecha de emisión';
    }

    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Por favor ingrese un monto válido';
    }

    if (!formData.front_image) {
      errors.front_image = 'La imagen frontal de la factura es requerida';
    }

    if (formData.tax_retention === null || formData.tax_retention === undefined || formData.tax_retention < 0) {
      errors.tax_retention = 'Por favor ingrese una retención de impuestos válida (puede ser 0)';
    }

    if (!formData.tax_amount || formData.tax_amount < 0) {
      errors.tax_amount = 'Por favor ingrese un monto de impuestos válido';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      // Create form data for file upload
      const formDataObj = new FormData();
      
      // Add invoice data as JSON string
      const invoiceData: InvoiceCreate = {
        // debtor_id: null, // Company will be assigned by admin during verification
        invoice_number: formData.invoice_number,
        issue_date: formData.issue_date ? formData.issue_date.toISOString().split('T')[0] : '',
        due_date: formData.due_date ? formData.due_date.toISOString().split('T')[0] : '',
        amount: formData.amount, // Send as string to be parsed by backend
        tax_retention: formData.tax_retention,
        tax_amount: formData.tax_amount.toString(), // Send as string to be parsed by backend
        currency: formData.currency,
        description: formData.description,
      };
      
      formDataObj.append('invoice_data', JSON.stringify(invoiceData));
      
      // Add files
      if (formData.front_image) {
        formDataObj.append('front_image', formData.front_image);
      }
      
      if (formData.back_image) {
        formDataObj.append('back_image', formData.back_image);
      }
      
      // Submit form
      const response = await apiService.post('/invoices', formDataObj, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      

      setSuccess(true);
      
      // Redirect after a short delay
      setTimeout(() => {
        navigate('/invoices');
      }, 2000);
      
    } catch (err: any) {
      console.error('Error uploading invoice:', err);
      
      // Check if the response contains a specific error message
      if (err.response?.data?.detail) {
        // If the error contains "unsupported operand type" but the invoice was actually created
        if (err.response.data.detail.includes("unsupported operand type") || 
            err.response.data.detail.includes("Datetimes provided to dates")) {

          setSuccess(true);
          
          // Redirect after a short delay
          setTimeout(() => {
            navigate('/invoices');
          }, 2000);
          return;
        }
        
        setError(err.response.data.detail);
      } else {
        setError('Error al subir la factura. Por favor intente nuevamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 4, maxWidth: 800, mx: 'auto', mt: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Subir Nueva Factura
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          ¡Factura subida exitosamente! Redirigiendo...
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Invoice Images */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Imágenes de la Factura
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    mb: 1,
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f9f9f9',
                    backgroundImage: frontImagePreview ? `url(${frontImagePreview})` : 'none',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {!frontImagePreview && (
                    <>
                      <UploadIcon sx={{ fontSize: 40, color: '#aaa', mb: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        Frente de la Factura (Requerido)
                      </Typography>
                    </>
                  )}
                </Box>
                <Button
                  component="label"
                  variant="contained"
                  fullWidth
                >
                  Subir Imagen Frontal
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'front')}
                  />
                </Button>
                {formErrors.front_image && (
                  <FormHelperText error>{formErrors.front_image}</FormHelperText>
                )}
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    border: '1px dashed #ccc',
                    borderRadius: 1,
                    p: 2,
                    textAlign: 'center',
                    mb: 1,
                    height: 200,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    backgroundColor: '#f9f9f9',
                    backgroundImage: backImagePreview ? `url(${backImagePreview})` : 'none',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                  }}
                >
                  {!backImagePreview && (
                    <>
                      <UploadIcon sx={{ fontSize: 40, color: '#aaa', mb: 1 }} />
                      <Typography variant="body2" color="textSecondary">
                        Reverso de la Factura (Opcional)
                      </Typography>
                    </>
                  )}
                </Box>
                <Button
                  component="label"
                  variant="outlined"
                  fullWidth
                >
                  Subir Imagen Trasera
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, 'back')}
                  />
                </Button>
              </Grid>
            </Grid>
          </Grid>
          
          {/* Invoice Details */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
              Detalles de la Factura
            </Typography>
          </Grid>
          
          {/* Company Selection - Removed: Admin will assign during verification */}
          
          {/* Invoice Number */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Número de Factura"
              name="invoice_number"
              value={formData.invoice_number}
              onChange={handleInputChange}
              error={!!formErrors.invoice_number}
              helperText={formErrors.invoice_number}
              required
            />
          </Grid>
          
          {/* Issue Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Fecha de Emisión"
                format="dd/MM/yyyy"
                value={formData.issue_date}
                onChange={(date) => handleDateChange(date, 'issue_date')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!formErrors.issue_date,
                    helperText: formErrors.issue_date,
                    required: true,
                    placeholder: "DD/MM/YYYY"
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Fecha de Vencimiento"
                format="dd/MM/yyyy"
                value={formData.due_date}
                onChange={(date) => handleDateChange(date, 'due_date')}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!formErrors.due_date,
                    helperText: formErrors.due_date,
                    required: true,
                    placeholder: "DD/MM/YYYY"
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          
          {/* Amount */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monto"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleInputChange}
              error={!!formErrors.amount}
              helperText={formErrors.amount}
              required
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {formData.currency === 'PYG' ? '₲' : '$'}
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          
          {/* Tax Retention */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="tax-retention-label">Retención de Impuestos</InputLabel>
              <Select
                labelId="tax-retention-label"
                id="tax_retention"
                name="tax_retention"
                value={formData.tax_retention}
                onChange={handleSelectChange}
                label="Retención de Impuestos"
                error={!!formErrors.tax_retention}
              >
                <MenuItem value={0}>0%</MenuItem>
                <MenuItem value={30}>30%</MenuItem>
                <MenuItem value={70}>70%</MenuItem>
              </Select>
              <FormHelperText error={!!formErrors.tax_retention}>
                {formErrors.tax_retention || "Porcentaje de impuestos retenidos por el deudor"}
              </FormHelperText>
            </FormControl>
          </Grid>
          
          {/* Tax Amount */}
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Monto de Impuestos"
              name="tax_amount"
              value={formData.tax_amount}
              onChange={handleInputChange}
              error={!!formErrors.tax_amount}
              helperText={formErrors.tax_amount}
              type="number"
            />
          </Grid>
          
          {/* Currency */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel id="currency-label">Moneda</InputLabel>
              <Select
                labelId="currency-label"
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleSelectChange}
                label="Moneda"
              >
                <MenuItem value="PYG">Guaraní Paraguayo (PYG)</MenuItem>
                <MenuItem value="USD">Dólar Estadounidense (USD)</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {/* Description */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Descripción"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              multiline
              rows={4}
              placeholder="Ingrese detalles adicionales sobre esta factura"
            />
          </Grid>
          
          {/* Submit Button */}
          <Grid item xs={12} sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button
                variant="outlined"
                onClick={() => navigate('/invoices')}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : null}
              >
                {loading ? 'Subiendo...' : 'Subir Factura'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Paper>
  );
};

export default InvoiceUploadForm;
